import React, { useState } from "react";
import dayjs from 'dayjs';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from "recharts";

// Crypto images URL
const cryptoImages = {
  PTGC: "./images/logoIcon.svg",
  UFO: "./images/UFO_big_circle.svg",
  BTC: "./images/Bitcoin.svg.webp",
  PLS: "./images/PLS-LogoTransparent_1.webp",
  ETH: "./images/ethereum-eth.svg",
  SOL: "./images/solana.png"
};

const getColor = (coin) => {
  switch (coin) {
   case "PTGC": return "#97FB1D";
    case "UFO": return "#A2FB36";
    case "BTC": return "#D8FDAB";
    case "PLS": return "#d8ffa9";
    case "ETH": return "#A2FB36";
    case "SOL": return "#D8FDAB";
    default: return "#fff";
  }
};

const timeframes = {"1w": "1W", "1m": "1M", "3m": "3M", "6m": "6M", "1y": "1Y", "all": "All"};

const CustomTooltip = ({ active, payload, label }) => {

  if (!active || !payload || !payload.length) return null;
  const item = payload[0].payload;

  return (
    <div style={{ backgroundColor: "#222", padding: 10, borderRadius: 8, color: "#fff" }}>
      <strong>{dayjs(label).format('DD MMM, YYYY')}</strong>
      <div style={{ marginTop: 5 }}>
        {["PTGC", "UFO", "BTC", "PLS", "ETH", "SOL"].map((coin) => (
          <div key={coin} style={{ marginBottom: 4 }}>
            <span style={{ color: getColor(coin), fontWeight: "bold" }}>{coin}</span><br />
            Growth: {item[`${coin}_price`].toFixed(2)}%
          </div>
        ))}
      </div>
    </div>
  );
};

export default function MultiAssetChart({fullData}) {
  const [timeframe, setTimeframe] = useState("3m");
  const [hoveredData, setHoveredData] = useState(null);

  const filteredData = (() => {
    const lastIndex = fullData.length - 1;
    switch (timeframe) {
      case "1w":
        return fullData.slice(Math.max(0, lastIndex - 7));
      case "1m":
        return fullData.slice(Math.max(0, lastIndex - 30));
      case "3m":
        return fullData.slice(Math.max(0, lastIndex - 90));
      case "6m":
        return fullData.slice(Math.max(0, lastIndex - 180));
      case "1y":
        return fullData.slice(Math.max(0, lastIndex - 365));
      case "all":
      default:
        return fullData;
    }
  })();

  const base = filteredData[0];

  const percentData = filteredData.map(entry => {
    const obj = { date: entry.date };
    ["PTGC", "UFO", "BTC", "PLS", "ETH", "SOL"].forEach(coin => {
      const key = `${coin}_price`;
      obj[key] = ((entry[key] - base[key]) / base[key]) * 100;
    });
    return obj;
  });

  return (
    <div style={{
      width: "100%",
      height: 600,
      backgroundColor: "#111",
      borderRadius: 12,
      padding: 10,
      color: "#fff",
      display: "flex",
      gap: 10,
      alignItems: "center",
      position: "relative",
      overflow: "hidden"
    }}>
      <div style={{ flex: 1, height: "100%" }} className="chart-container">
        <div className='d-flex'>
          <div className='rTabs' style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 20 }}>
            {Object.keys(timeframes).map(tf => (
              <button
                key={tf}
                onClick={() => setTimeframe(tf)}
                style={{
                  padding: '8px 16px',
                  background: timeframe === tf
                    ? 'radial-gradient(62.23% 424.31% at 68.62% 42.59%, #76EA10 0%, #2FCD3F 100%)'
                    : 'transparent',
                  color: '#FFF',
                  border: 'none',
                  cursor: 'pointer',
                  borderRadius: 4,
                  fontWeight: 'bold',
                  transition: 'background 0.3s ease',
                  whiteSpace: 'nowrap',
                }}
              >
                {timeframes[tf]}
              </button>
            ))}
          </div>
        </div>

        <ResponsiveContainer width="100%" height="90%">
          <AreaChart
            data={percentData}
            onMouseMove={(state) => {
              if (state && state.activePayload && state.activePayload.length > 0) {
                setHoveredData(state.activePayload[0].payload);
              }
            }}
            onMouseLeave={() => setHoveredData(null)}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#333" />
            <XAxis dataKey="date" stroke="#ccc" tick={{ fill: '#ccc', fontSize: 12 }} tickFormatter={date => dayjs(date).format('DD MMM, YYYY')}/>

            <YAxis stroke="#ccc" tick={{ fill: "#ccc" }} unit="%" />
            <Tooltip content={<CustomTooltip />} isAnimationActive={false} />
            <Legend wrapperStyle={{ color: "#fff" }} />
            <Area type="monotone" dataKey="PTGC_price" stroke="#97FB1D" fillOpacity={0.2} fill="#97FB1D" />
            <Area type="monotone" dataKey="UFO_price" stroke="#A2FB36" fillOpacity={0.2} fill="#A2FB36" />
            <Area type="monotone" dataKey="BTC_price" stroke="#D8FDAB" fillOpacity={0.2} fill="#D8FDAB" />
            <Area type="monotone" dataKey="PLS_price" stroke="#d8ffa9" fillOpacity={0.2} fill="#d8ffa9" />
            <Area type="monotone" dataKey="ETH_price" stroke="#A2FB36" fillOpacity={0.2} fill="#A2FB36" />
            <Area type="monotone" dataKey="SOL_price" stroke="#D8FDAB" fillOpacity={0.2} fill="#D8FDAB" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      <div style={{
        position: 'absolute',
        left: '125px',
        top: '80px',
        zIndex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 10
      }}>
        {["PTGC", "UFO", "BTC", "PLS", "ETH", "SOL"].map((coin) => {
          const dataPoint = hoveredData ?? percentData[percentData.length - 1];
          const growth = dataPoint ? dataPoint[`${coin}_price`] : 0;
          const growthColor = growth >= 0 ? '#00FF00' : '#FF4444';
          const sign = growth >= 0 ? '+' : '';
          return (
            <div key={coin} style={{ textAlign: "center" }}>
              <img
                src={cryptoImages[coin]}
                alt={`${coin} logo`}
                style={{ width: 40, height: 40, borderRadius: "50%" }}
              />
              <div>
                <span style={{ fontWeight: "bold", color: getColor(coin) }}>{coin}</span>
                <span style={{ color: growthColor, fontSize: "0.9em", fontWeight: "bold" }}>
                  {" "}{sign}{growth.toFixed(2)}%
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
